import { useEffect, useState } from "react";
import { Users, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { PermCell, PermFilters, PermTable, usePermFilter } from "./PermTable";

/** Audience × permission defaults — the first layer of the access model. */
export default function AudienceMatrix({ catalog, canManage, audiences, onChanged }) {
  const filter = usePermFilter(catalog);
  const [counts, setCounts] = useState({});
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/app-audiences/reach").then((r) => setCounts(r.data.counts || {})).catch(() => {});
  }, []);

  const cols = (audiences || []).filter((a) => a.active !== false && a.audience_id !== "super").map((a) => ({
    id: a.audience_id, label: a.label,
    sub: `${counts[a.audience_id] ?? "–"} people`,
    title: `${a.label} — ${(a.base_roles || []).join(", ")}${a.partner_types?.length ? ` · ${a.partner_types.join(", ")}` : ""}`,
  }));

  const toggle = async (aud, perm) => {
    if (!canManage) return;
    const has = (aud.permissions || []).includes(perm);
    const key = `${aud.audience_id}|${perm}`;
    setBusy(key);
    try {
      await api.post(`/app-audiences/${aud.audience_id}/permissions/toggle`, { perm, grant: !has });
      toast.success(`${has ? "Removed" : "Added"} ${perm} ${has ? "from" : "to"} ${aud.label} defaults`, { id: key });
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail), { id: key });
    } finally { setBusy(""); }
  };

  return (
    <div data-testid="audience-matrix">
      <div className="mb-3 flex flex-wrap items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-xs text-slate-600">
        <Users className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
        <div className="min-w-0 flex-1">
          <span className="font-medium text-slate-800">Audiences are who; these are their defaults.</span>{" "}
          Everyone in an audience starts with every permission ticked here. Roles and individual people can then add or remove on top.
          Ticking an app&apos;s <span className="font-mono">view</span> here is the same as picking that audience on the app.
        </div>
        <Link to="/settings?tab=appdefaults" className="inline-flex items-center gap-1 text-primary hover:underline" data-testid="audience-matrix-manage-link">
          Manage the audience list <ExternalLink className="h-3 w-3" />
        </Link>
      </div>
      <PermFilters filter={filter} testid="audience-filter" />
      <Card className="border-slate-200 shadow-sm">
        <PermTable groups={filter.shown} columns={cols} testid="audience-table"
          renderCell={(perm, col) => {
            const aud = audiences.find((a) => a.audience_id === col.id);
            const on = (aud?.permissions || []).includes(perm);
            return (
              <PermCell state={on ? "granted" : "none"} disabled={!canManage || busy === `${col.id}|${perm}`}
                onClick={() => toggle(aud, perm)} testid={`aud-${col.id}-${perm}`}
                title={`${on ? "Remove" : "Add"} ${perm} ${on ? "from" : "to"} ${aud?.label}`} />
            );
          }} />
      </Card>
    </div>
  );
}
